/**
 * Pure rules shared by LessonSequencingPage. Kept out of the component so the
 * mutation-routing and curriculum-cascade logic stays testable without a DOM.
 */

/** Sequencing only ever schedules Mon–Fri, so a week is at most 5 days. */
export const MAX_DAYS_PER_WEEK = 5;
/** classroom_sequencing_settings caps the daily assignment count at 3. */
export const MAX_LESSONS_PER_DAY = 3;

/**
 * The values the Lesson settings form opens on.
 *
 * The teacher's own settings, not a group's. The save is teacher-wide and
 * clears every group's stored pace, so seeding from whichever group happened
 * to be selected would take that group's values, write them to the default and
 * push them onto all the others -- a teacher opening the dialog on an
 * overridden group and changing one control would silently re-pace the rest.
 * That is the state `apply_to_all` exists to clean up, so it is exactly the
 * state the form must not read from.
 *
 * The weekly total is clamped to what the weekly picker can express. A
 * classroom saved on the daily branch at two or three a day stores 10 or 15,
 * and seeding the weekly draft with that offers a value none of the 1-5
 * buttons can show -- and saves it, if the teacher switches branches without
 * touching them.
 *
 * In No expectation mode the server reports `incomplete_mode` as the forced
 * "completion" and the teacher's own choice as `paced_incomplete_mode`. The
 * form seeds from the choice: it saves every field, so seeding the forced
 * value would write it over the choice on the way out of the mode.
 *
 * @param {{ expected_lessons_per_day: number, expected_lessons_per_week: number,
 *   incomplete_mode: string, paced_incomplete_mode?: string | null,
 *   assignment_mode?: string | null } | null | undefined} teacherSettings
 * @returns {{ daily: number, weekly: number, mode: string, assignmentMode: string } | null}
 */
export function settingsFormSeed(teacherSettings) {
  if (!teacherSettings) return null;
  return {
    daily: teacherSettings.expected_lessons_per_day,
    weekly: Math.min(
      teacherSettings.expected_lessons_per_week,
      MAX_DAYS_PER_WEEK,
    ),
    mode:
      teacherSettings.paced_incomplete_mode ?? teacherSettings.incomplete_mode,
    assignmentMode: teacherSettings.assignment_mode ?? "weekly",
  };
}

/**
 * Lessons a week, derived from the two stored fields.
 *
 * `days_per_week` × `lessons_per_day` is the single source of truth (Keita's
 * call); the weekly figure Linear's copy talks about is a view of it, not a
 * third stored value that could drift out of step.
 *
 * @param {number} daysPerWeek
 * @param {number} lessonsPerDay
 * @returns {number}
 */
export function lessonsPerWeek(daysPerWeek, lessonsPerDay) {
  return Math.max(1, daysPerWeek || 1) * Math.max(1, lessonsPerDay || 1);
}

/**
 * Does this classroom hand out a week at a time?
 *
 * Reads the stored choice. It used to be derived, twice and inconsistently:
 * `isWeeklyMode(weeklyTotal)` asked whether the total was under five, and
 * `isWeeklyAssignment(lessonsPerDay)` asked whether the daily cap was one. At
 * the shipped default -- five days, one lesson a day -- the first said "daily"
 * and the second said "weekly", and the boundary was flipped and reverted more
 * than once trying to reconcile them.
 *
 * Neither could be right. The settings design offers two buckets, "5 or
 * fewer" and "More than 5", and both can produce exactly five lessons a week
 * -- five days at one a day, which the weekly bucket assigns with no daily cap
 * and the daily bucket assigns at a cap of one. The labels name the two ways
 * of reaching a pace, not two ranges of one total. Since both buckets reach
 * the same total, no function of the total can tell them apart. The server
 * stores the choice instead; this reads it.
 *
 * @param {"weekly" | "daily" | "none" | null | undefined} assignmentMode
 * @returns {boolean}
 */
export function isWeeklyAssignment(assignmentMode) {
  return (assignmentMode ?? "weekly") === "weekly";
}

/**
 * Is this No expectation mode (P-PRD-525)?
 *
 * One lesson at a time, moved on only by finishing it: no dates, no days, no
 * pace. Asked before `isWeeklyAssignment`, never instead of it -- that returns
 * false here, and every reader that takes false for "daily" would page days
 * and shift by days in a class that has none.
 *
 * @param {"weekly" | "daily" | "none" | null | undefined} assignmentMode
 * @returns {boolean}
 */
export function isPointerMode(assignmentMode) {
  return assignmentMode === "none";
}

/**
 * Is saving `next` over `stored` a switch into or out of No expectation mode?
 *
 * Such a save is not a settings save: the mode moves with every student's
 * starting point, through the review and `/lesson-sequencing/mode-switch`, and
 * the settings PATCH refuses it (409). Compared by value, as the server does --
 * the dialog sends every field, so the mode a teacher is already in rides
 * along unchanged.
 *
 * @param {"weekly" | "daily" | "none" | null | undefined} stored
 * @param {"weekly" | "daily" | "none" | null | undefined} next
 * @returns {boolean}
 */
export function isModeSwitch(stored, next) {
  return isPointerMode(stored ?? "weekly") !== isPointerMode(next ?? "weekly");
}

/**
 * The lesson the starting-point review offers a student when switching modes.
 *
 * Leaving No expectation mode, where the student is now: their Current, so the
 * new pace starts from where they are. Entering it, the server's entry point
 * -- the next lesson they have not finished, walking on from where they are
 * (R3).
 *
 * The stored placement suggestion only for a student never placed. For one who
 * was, it is where they first started, not where they are: a placed student
 * with no position -- past the last lesson -- is offered nothing and waits for
 * the teacher's pick rather than being restarted from the beginning.
 *
 * @param {{ status?: string | null, suggested_curriculum_node_id?: number | null,
 *   entry_curriculum_node_id?: number | null,
 *   current_lesson?: { curriculum_node_id?: number | null } | null,
 *   current?: Array<{ curriculum_node_id?: number | null }> }} student
 * @param {boolean} leaving
 * @returns {number | null}
 */
export function modeSwitchSeed(student, leaving) {
  if (leaving) {
    const current =
      student.current_lesson?.curriculum_node_id ??
      student.current?.[0]?.curriculum_node_id ??
      null;
    if (current != null) return current;
  }
  if (!leaving && student.entry_curriculum_node_id != null) {
    return student.entry_curriculum_node_id;
  }
  if (student.status && student.status !== "pending_placement") return null;
  return student.suggested_curriculum_node_id ?? null;
}

/**
 * The starting point a review row suggests: what the row is seeded with, what
 * its badge calls the suggestion, and what Reset puts back. One answer for all
 * three, so Reset never restores a different lesson than the row opened on.
 *
 * Activation, the stored placement suggestion. A mode switch, `modeSwitchSeed`.
 *
 * @param {Parameters<typeof modeSwitchSeed>[0]} student
 * @param {"weekly" | "daily" | "none" | null | undefined} switchTarget the mode
 *   being switched to, or nothing for an activation review
 * @param {boolean} [resumingPointer] keep the current lesson when re-enabling
 * @returns {number | null}
 */
export function reviewSuggestion(
  student,
  switchTarget,
  resumingPointer = false,
) {
  if (resumingPointer) {
    return student.status === "pending_placement"
      ? null
      : modeSwitchSeed(student, true);
  }
  if (switchTarget == null) return student.suggested_curriculum_node_id ?? null;
  return modeSwitchSeed(student, !isPointerMode(switchTarget));
}

/**
 * The `/lesson-sequencing/mode-switch` request: every group the review showed,
 * each with the starting points chosen in it.
 *
 * One request for all of them -- the switch is teacher-wide and lands in one
 * transaction or not at all (R4, R32). A group with no picks is still named,
 * so its students are left waiting for placement rather than forgotten.
 *
 * Leaving the mode, the pace goes in the same request, so a switch that fails
 * leaves the teacher's pace as it was rather than half of the change applied.
 *
 * @param {"weekly" | "daily" | "none"} assignmentMode
 * @param {Array<{ classroom_id: number, student_id: number }>} roster
 * @param {Record<string, number>} selections keyed by `${classroom_id}-${student_id}`
 * @param {{ expected_days_per_week: number, expected_lessons_per_day: number,
 *   expected_lessons_per_week: number, incomplete_mode: string } | null} [pace]
 * @param {number[]} [classroomIds] every group the review showed, including
 *   one with no students -- the switch refuses a group left out, and an empty
 *   group has no roster rows to be named by
 * @returns {{ assignment_mode: string, classrooms: Array<{ classroom_id: number,
 *   students: Array<{ student_id: number, curriculum_node_id: number }> }>,
 *   pace?: object }}
 */
export function modeSwitchBody(
  assignmentMode,
  roster,
  selections,
  pace = null,
  classroomIds = [],
) {
  const byClassroom = new Map(classroomIds.map((id) => [id, []]));
  for (const student of roster) {
    if (!byClassroom.has(student.classroom_id)) {
      byClassroom.set(student.classroom_id, []);
    }
    const node = selections[`${student.classroom_id}-${student.student_id}`];
    if (node != null) {
      byClassroom.get(student.classroom_id).push({
        student_id: student.student_id,
        curriculum_node_id: node,
      });
    }
  }
  return {
    assignment_mode: assignmentMode,
    classrooms: [...byClassroom].map(([classroom_id, students]) => ({
      classroom_id,
      students,
    })),
    ...(pace && {
      pace: {
        expected_days_per_week: pace.expected_days_per_week,
        expected_lessons_per_day: pace.expected_lessons_per_day,
        expected_lessons_per_week: pace.expected_lessons_per_week,
        incomplete_mode: pace.incomplete_mode,
      },
    }),
  };
}

/**
 * The single lesson every selected student is currently on, or null.
 *
 * Drives whether the bulk change-lesson picker opens with a value. Preselecting
 * when the selection spans different lessons is worse than opening empty: the
 * modal's first option is the first lesson in the *curriculum*, so a teacher who
 * clicks straight through sends the whole group back to lesson one.
 *
 * @param {Array<{ student_id: number, current?: Array<{ curriculum_node_id?: number | null }> }>} students
 * @param {Set<number>} selectedStudentIds
 * @returns {number | null}
 */
export function sharedCurrentLesson(students, selectedStudentIds) {
  const nodeIds = new Set();
  for (const student of students) {
    if (!selectedStudentIds.has(student.student_id)) continue;
    const nodeId = student.current?.[0]?.curriculum_node_id ?? null;
    // A student with nothing scheduled today has no current lesson, which is
    // itself a difference -- preselecting the others' lesson would quietly
    // decide for them too.
    if (nodeId == null) return null;
    nodeIds.add(nodeId);
    if (nodeIds.size > 1) return null;
  }
  return nodeIds.size === 1 ? [...nodeIds][0] : null;
}

/**
 * The *lesson* the same preselection names, for resolving it against the picker.
 *
 * `lesson_options` holds one entry per lesson: a curriculum that placed one
 * lesson at two slots is collapsed to the earliest, so a dropdown cannot show
 * the same title twice at different positions. Placement does not follow that
 * rule -- `ensure_schedule_current` gives a student whose frontier has passed
 * the first slot the second one -- so `sharedCurrentLesson` can name a node the
 * list does not carry, and matching the preselection by node alone found
 * nothing. The picker then rendered its placeholder over a real selection and
 * the modal previewed a lesson the teacher had not chosen.
 *
 * Matching by lesson finds the entry that stands for it, which is what the
 * dropdown renders and what decides whether Save is reachable. The write still
 * carries the preselected node, not this entry's: the two slots sit at
 * different sequence positions, so swapping them would relocate the student
 * backward over every lesson between the two -- a change the confirmation
 * summary, which compares against the preselected node, would report as "No
 * change". `change_lesson` accepts either copy.
 *
 * @param {Array<{ student_id: number, current?: Array<{ lesson_id?: number | null }> }>} students
 * @param {Set<number>} selectedStudentIds
 * @returns {number | null}
 */
export function sharedCurrentLessonId(students, selectedStudentIds) {
  const lessonIds = new Set();
  for (const student of students) {
    if (!selectedStudentIds.has(student.student_id)) continue;
    const lessonId = student.current?.[0]?.lesson_id ?? null;
    if (lessonId == null) return null;
    lessonIds.add(lessonId);
    if (lessonIds.size > 1) return null;
  }
  return lessonIds.size === 1 ? [...lessonIds][0] : null;
}

/**
 * Truncate a long curriculum path from the LEFT, so the lesson-specific tail
 * stays visible. The full path belongs in a title attribute alongside.
 *
 * @param {string} value
 * @param {number} maxLength
 * @returns {string}
 */
export function truncateMiddle(value, maxLength) {
  const text = value ?? "";
  if (text.length <= maxLength) return text;
  // Too small to hold the ellipsis and any text either side of it. Slicing
  // here would reach slice(-0) === slice(0) at 1, returning the whole string —
  // longer than both the budget and, with the ellipsis, the input itself.
  if (maxLength <= 1) return maxLength < 1 ? "" : "…";
  // Both ends carry meaning, so both survive. These labels are curriculum
  // paths — "Grade 1 › General › <lesson name>" — where the head names the
  // grade and the tail names the lesson. Dropping the head made every lesson
  // in one long-named unit render as the same string, so a teacher picking a
  // starting point saw several identical options and could not tell them
  // apart; dropping the tail would lose the lesson name instead.
  //
  // The ellipsis counts against the budget the parameter names, so the two
  // slices share one character fewer rather than returning maxLength + 1.
  const budget = maxLength - 1;
  const head = Math.ceil(budget / 2);
  const tail = budget - head;
  // slice(-0) is the whole string, so an empty tail is spliced in by hand.
  return tail === 0
    ? `${text.slice(0, head)}…`
    : `${text.slice(0, head)}…${text.slice(-tail)}`;
}

/** Whitespace-split name words, with blanks dropped. */
function nameParts(name) {
  return (name ?? "").trim().split(/\s+/).filter(Boolean);
}

/**
 * Initials for a roster avatar: first name + last name, or just the one
 * initial when a student has a single-word name.
 *
 * @param {string} name
 * @returns {string}
 */
export function getInitials(name) {
  const parts = nameParts(name);
  if (!parts.length) return "?";
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
  return `${first}${last}`.toUpperCase();
}

/** Avatar tints, picked by name so a student keeps the same colour. */
export const AVATAR_PALETTE = [
  { backgroundColor: "#E8F0FE", color: "#1967D2" },
  { backgroundColor: "#FCE8E6", color: "#B3261E" },
  { backgroundColor: "#FEF7E0", color: "#9A5B00" },
  { backgroundColor: "#F3E8FD", color: "#7B1FA2" },
  { backgroundColor: "#E0F2F1", color: "#00796B" },
  { backgroundColor: "#FCE8F3", color: "#A91E63" },
];

/**
 * Stable palette index for a name.
 *
 * @param {string} name
 * @returns {number}
 */
export function avatarPaletteIndex(name) {
  const total = [...(name ?? "")].reduce(
    (sum, character) => sum + character.charCodeAt(0),
    0,
  );
  return total % AVATAR_PALETTE.length;
}

/**
 * @typedef {{ curriculum_node_id: number, depth: number, label: string, name: string }} PathNode
 * @typedef {{ curriculum_node_id: number, title: string, path: PathNode[] }} Option
 */

/**
 * Does this option's curriculum path start with `prefix` (a list of node ids)?
 *
 * Keyed on `curriculum_node_id`, not name: nothing stops a curriculum from
 * having two siblings with the same name, and matching on names collapsed them
 * into one entry and made the second subtree unreachable.
 *
 * @param {Option} option
 * @param {number[]} prefix
 * @returns {boolean}
 */
function matchesPrefix(option, prefix) {
  return prefix.every(
    (nodeId, index) => option.path?.[index]?.curriculum_node_id === nodeId,
  );
}

/**
 * Was this skip set for a day still in the future when it was recorded?
 *
 * Prefers the `scheduled` flag the server now writes. Events recorded before
 * that field existed carry only `skipped_on`, and treating its absence as "not
 * scheduled" described a still-future skip as one that had already happened.
 *
 * Falls back to comparing the skipped day against the day the event was
 * written, which is the same comparison the server makes and needs no backfill:
 * an event is a record of a moment, so the moment is already in it.
 *
 * @param {{ created_at: string, payload?: Record<string, unknown> }} event
 * @returns {boolean}
 */
function wasScheduledAhead(event) {
  const payload = event?.payload ?? {};
  if (typeof payload.scheduled === "boolean") return payload.scheduled;
  if (!payload.skipped_on || !event?.created_at) return false;
  // Both reduced to a YYYY-MM-DD so a timestamp's clock time cannot tip the
  // comparison a day either way.
  const recordedOn = new Date(event.created_at).toISOString().slice(0, 10);
  return String(payload.skipped_on) > recordedOn;
}

/**
 * One audit-banner line for a sequencing event.
 *
 * The server records what happened as an action plus a payload; this turns that
 * into the sentence the export's banner shows -- "<what> by <who> on <date>".
 * Kept pure so the wording is testable without a DOM, and central so the banner
 * cannot describe an action differently from the toast that fired it.
 *
 * Unknown actions fall back to the action name rather than being dropped: a
 * banner that silently omits events is worse than one that shows a plain label
 * for a new one.
 *
 * @param {{ action: string, actor_name?: string | null, created_at: string, payload?: Record<string, unknown> }} event
 * @param {(iso: string) => string} formatDay  Renders a YYYY-MM-DD as a day label.
 * @returns {string}
 */
export function describeSequencingEvent(event, formatDay) {
  const payload = event?.payload ?? {};
  const day = payload.skipped_on ? formatDay(String(payload.skipped_on)) : "";
  const what = (() => {
    switch (event?.action) {
      case "skip_day":
        if (payload.removed) return `Scheduled skip removed for ${day}`;
        return wasScheduledAhead(event)
          ? `Skip scheduled for ${day}`
          : `${day} skipped`;
      case "shift_days":
        return `Moved 1 day ${Number(payload.delta) < 0 ? "back" : "forward"}`;
      case "change_lesson":
        return "Changed current lesson";
      case "approve_placement":
        return "Approved a starting lesson";
      case "enable":
        return "Turned auto-sequencing on";
      case "disable":
        return "Turned auto-sequencing off";
      case "settings_update":
        return "Updated lesson settings";
      // Written by the curriculum binding fan-out, not by a teacher, so these
      // two carry no actor and the line reads "... on Sep 4" with no author.
      // Named for what the teacher sees change -- the group's lesson sequence
      // -- rather than for the organization binding that caused it, which is
      // an administrator's concept in another app.
      // The automatic daily fill, one line per run rather than per lesson. Like
      // the two below it this carries no actor -- it runs inside whichever read
      // arrived first, and that reader did not decide the schedule -- so the
      // line reads "... on Sep 16" with no author. Named for what the teacher
      // sees appear on the roster, not for the function that wrote it.
      case "place": {
        const placed = Number(payload.row_count) || 0;
        // The carryover moved work the student already had; it did not
        // schedule anything new, and a teacher reading "Scheduled 2 lessons"
        // for it would go looking for two lessons that were never added.
        if (payload.source === "weekly_carryover") {
          return `Carried ${placed} unfinished lesson${placed === 1 ? "" : "s"} into the week`;
        }
        const dates = Array.isArray(payload.dates) ? payload.dates : [];
        const first = dates.length ? formatDay(String(dates[0])) : "";
        const last =
          dates.length > 1 ? formatDay(String(dates[dates.length - 1])) : "";
        const span = first ? ` for ${last ? `${first}–${last}` : first}` : "";
        // The reflow moved lessons the student already had, to keep the dates
        // running in sequence order once a lesson published into the middle of
        // the week. Nothing was added, so it needs the same separation the
        // carryover gets above: a teacher reading "Scheduled 4 lessons" here
        // would go looking for four lessons that do not exist. The dates are
        // kept because they are the useful part -- they say what moved where.
        if (payload.source === "weekly_window_reflow") {
          return `Rescheduled ${placed} lesson${placed === 1 ? "" : "s"}${span} to fit a new lesson`;
        }
        return `Scheduled ${placed} lesson${placed === 1 ? "" : "s"}${span}`;
      }
      case "curriculum_attached":
        return "Lesson sequence assigned";
      case "curriculum_detached":
        return "Lesson sequence removed";
      default:
        return String(event?.action ?? "Changed lesson sequencing");
    }
  })();
  const who = event?.actor_name ? ` by ${event.actor_name}` : "";
  const when = new Date(event.created_at).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  return `${what}${who} on ${when}`;
}

/**
 * The curriculum's level labels, root-first: "Grade", "Mission", … "Lesson".
 *
 * Read off the options, not off whatever happens to be selected. Deriving the
 * hierarchy from a selection makes the shape of the picker depend on whether
 * anything is picked yet -- with nothing chosen there is no path, so the picker
 * collapsed to one flat "Lesson" list of every lesson in the curriculum and the
 * Grade field only appeared *after* a choice. The hierarchy is a property of the
 * curriculum and is known before the teacher touches anything.
 *
 * Longest path wins, so one malformed option cannot shorten the scaffold.
 *
 * @param {Option[]} options
 * @returns {string[]}
 */
export function curriculumLevels(options) {
  let deepest = [];
  for (const option of options ?? []) {
    const path = option?.path ?? [];
    if (path.length > deepest.length) deepest = path;
  }
  return deepest.map((node) => node.label);
}

/**
 * Distinct curriculum nodes available one level below `prefix`, in sequence
 * order. Drives one dropdown of the cascading lesson picker: the id is the
 * value, the name is only the label.
 *
 * @param {Option[]} options
 * @param {number[]} prefix
 * @returns {Array<{ curriculum_node_id: number, name: string }>}
 */
export function pathNodesAtDepth(options, prefix) {
  const depth = prefix.length;
  const nodes = [];
  const seen = new Set();
  for (const option of options) {
    if (!matchesPrefix(option, prefix)) continue;
    const node = option.path?.[depth];
    if (!node || seen.has(node.curriculum_node_id)) continue;
    seen.add(node.curriculum_node_id);
    nodes.push({
      curriculum_node_id: node.curriculum_node_id,
      name: node.name,
    });
  }
  return nodes;
}

/**
 * First option reachable by extending `prefix` with `nodeId`. Picking a level
 * has to resolve to a concrete lesson, since that is what the API takes.
 *
 * @param {Option[]} options
 * @param {number[]} prefix
 * @param {number} nodeId
 * @returns {Option | null}
 */
export function findOptionForPath(options, prefix, nodeId) {
  const depth = prefix.length;
  return (
    options.find(
      (option) =>
        matchesPrefix(option, prefix) &&
        option.path?.[depth]?.curriculum_node_id === nodeId,
    ) ?? null
  );
}

/**
 * The single classroom a bulk change-lesson may target, or null when the
 * selection is empty or spans groups. A curriculum_node_id is only meaningful
 * inside its own classroom's sequence, so cross-group bulk edits are refused
 * rather than sent to the wrong sequence.
 *
 * @param {Array<{ student_id: number, classroom_id: number }>} students
 * @param {Set<number>} selectedStudentIds
 * @returns {number | null}
 */
export function changeTargetClassroom(students, selectedStudentIds) {
  const ids = new Set(
    students
      .filter((student) => selectedStudentIds.has(student.student_id))
      .map((student) => student.classroom_id),
  );
  return ids.size === 1 ? [...ids][0] : null;
}

/**
 * The selected students a bulk action on `classroomId` reaches, in roster order.
 *
 * One derivation for a handler and the dialog that counts for it, so the two
 * cannot disagree. They did: "Move 1 student's schedule 1 day back?" counted
 * by the selection's group while its handler filtered by the page's group and
 * sent nobody (GDS Group B, 2026-09-29, each student in a group of their own).
 * No classroom reaches no one.
 *
 * For the selection's own group -- `changeTargetClassroom` -- the classroom test
 * excludes nobody, since that group is only set when every selected student
 * shares it. It is live for the page's group, which Skip day acts on and a
 * selection may only partly overlap.
 */
export function selectedStudentIdsIn(
  students,
  selectedStudentIds,
  classroomId,
) {
  if (classroomId == null) return [];
  return students
    .filter(
      (student) =>
        selectedStudentIds.has(student.student_id) &&
        student.classroom_id === classroomId,
    )
    .map((student) => student.student_id);
}

/**
 * Who a pending change-lesson touches, and what it does to each of them.
 *
 * Drives the confirmation table, so it answers the question a teacher asks
 * before a group-wide write: which students move, off what, and which are
 * already there. Students outside `classroomId` are dropped -- the write only
 * ever reaches one classroom's sequence, so counting them would overstate it.
 *
 * A student already on `targetNodeId` is kept and marked `unchanged` rather
 * than filtered out: a select-all over a drifted roster usually catches a few,
 * and hiding them makes the table disagree with the roster behind it.
 *
 * Reads `current_lesson`, not `current[0]`. Those are the same lesson only
 * while the roster sits on today; page it forward and `current` becomes the
 * focused day's work, whereas `change_lesson` displaces the lesson the
 * sequence pointer names whatever day is on screen. Comparing against the
 * focused row would show a future lesson as the one being replaced, and could
 * mark a student "No change" while the write moved a different lesson of
 * theirs to Incomplete.
 *
 * @param {Array<{ student_id: number, classroom_id: number, name: string,
 *   current_lesson?: { curriculum_node_id?: number | null, title: string } | null,
 *   current: Array<{ curriculum_node_id?: number | null, title: string }> }>} students
 * @param {Set<number>} selectedStudentIds
 * @param {number | null} classroomId
 * @param {number | null} targetNodeId
 * @returns {Array<{ studentId: number, name: string, fromTitle: string,
 *   unchanged: boolean }>}
 */
export function changeLessonImpact(
  students,
  selectedStudentIds,
  classroomId,
  targetNodeId,
) {
  // The same list `changeLesson` sends, so the table cannot name a student the
  // write skips or skip one it moves.
  const reached = new Set(
    selectedStudentIdsIn(students, selectedStudentIds, classroomId),
  );
  return students
    .filter((student) => reached.has(student.student_id))
    .map((student) => {
      // `current[0]` only as a fallback, for a response predating
      // `current_lesson`. On today's column the two agree.
      const from = student.current_lesson ?? student.current?.[0] ?? null;
      const fromNodeId = from?.curriculum_node_id ?? null;
      return {
        studentId: student.student_id,
        name: student.name,
        // Named rather than left blank: "no current lesson" is a real state
        // for a student who has finished their week.
        fromTitle: from?.title ?? "No current lesson",
        unchanged: fromNodeId != null && fromNodeId === targetNodeId,
      };
    });
}

/**
 * Has this student's starting point been reviewed? The backend approves some
 * placements automatically, and those count as reviewed.
 *
 * @param {"suggested" | "auto_approved" | "approved"} placementStatus
 * @returns {boolean}
 */
export function isPlacementReviewed(placementStatus) {
  return placementStatus === "approved" || placementStatus === "auto_approved";
}

/**
 * The badges the activation review puts on each row.
 *
 * @type {Record<string, {label: string, bg: string, fg: string}>}
 */
export const STARTING_POINT_BADGES = {
  needsLesson: { label: "Needs lesson", bg: "#FDECEC", fg: "#B42318" },
  notInSequence: { label: "Not in sequence", bg: "#FDECEC", fg: "#B42318" },
  modified: { label: "Modified", bg: "#FFF4E5", fg: "#9A5B00" },
  chosen: { label: "Chosen", bg: "#EEF3F0", fg: "#42566B" },
  suggested: { label: "Suggested", bg: "#EAF7EF", fg: "#137A55" },
};

/**
 * Which state a row of the activation review is in.
 *
 * Two of the five block activation and have to be findable: a row with nothing
 * selected, and one naming a lesson this group's sequence cannot offer. The
 * other three say who decided. "Suggested" is the system's own proposal,
 * untouched. "Modified" is that proposal changed. "Chosen" is a human's
 * decision, which covers both a lesson picked where the system had none to
 * offer and a placement approved in an earlier review.
 *
 * That last case used to have a badge of its own, "Approved", so a roster
 * showed some rows Approved and others Chosen with nothing on screen to
 * explain the difference -- the split was `placement_status`, history the
 * teacher cannot see. `auto_approved` still reads as "Suggested" when the row
 * is untouched, because that status *is* the system placing the student.
 *
 * `placement_status` decides this rather than the node ids, because activation
 * writes the teacher's own choice back into `suggested_curriculum_node_id`:
 * comparing ids would relabel their decision as ours.
 *
 * @param {{selected: number|null, suggested: number|null, isOffered: boolean,
 *   placementStatus: string|undefined}} row
 * @returns {{label: string, bg: string, fg: string}}
 */
export function startingPointBadge({
  selected,
  suggested,
  isOffered,
  placementStatus,
}) {
  if (!selected) return STARTING_POINT_BADGES.needsLesson;
  if (!isOffered) return STARTING_POINT_BADGES.notInSequence;
  if (suggested && selected !== suggested)
    return STARTING_POINT_BADGES.modified;
  if (!suggested || placementStatus === "approved") {
    return STARTING_POINT_BADGES.chosen;
  }
  return STARTING_POINT_BADGES.suggested;
}

/**
 * Has this row anything to reset -- does it differ from what it opened as?
 *
 * Gating this on "differs from the suggestion" left the button dead for every
 * row that has no suggestion, which is exactly the row a teacher is most
 * likely to mis-pick: the student the derivation could not place. Those rows
 * open empty, so any pick is a change and can be taken back.
 *
 * @param {number|null} selected
 * @param {number|null} suggested
 * @returns {boolean}
 */
export function canResetStartingPoint(selected, suggested) {
  return (selected ?? null) !== (suggested ?? null);
}

/**
 * What a row's selection becomes when reset: the suggestion where there is
 * one, and no selection at all where there is not.
 *
 * @param {number|null} suggested
 * @returns {number|undefined}
 */
export function resetStartingPoint(suggested) {
  return suggested || undefined;
}
